import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { LocationService } from '../services/location.service';
import { AuthService } from '../services/auth.service';
import { CORPORATE_ADMIN } from '../constants';
import { Observable, catchError, from, map, of, switchMap, throwError } from 'rxjs';
import { LocationPermissionModel } from '../models/location.model';
import PPURouteBuilder from '../utilities/route.builder';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivateChild {
  constructor(
    private router: Router,
    private locationService: LocationService,
    private authService: AuthService,
    private translate: TranslateService
  ) {}

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return from(this.authService.getAgentInfo()).pipe(
      switchMap((agent) => {
        // Corporate tokens identify a parent, so they do not have a child permission to resolve here.
        if (agent.role === CORPORATE_ADMIN) {
          return of(null as LocationPermissionModel | null);
        }

        return this.locationService.GetUserLocationPermission();
      }),
      map((x) => {
        if (!x) return true;
        const [result, route] = PPURouteBuilder.isValidRoute(x, state.url);
        if (result) return true;
        else {
          this.router.navigate([route]);
          return true;
        }
      }),
      catchError((error) => {
        // Let the shell render its logout screen only for the expected no-location case.
        if (this.isNoLocationAccessError(error)) return of(true);
        return throwError(() => error);
      })
    );
  }

  private isNoLocationAccessError(error: unknown): boolean {
    return Array.isArray(error)
      && error.length === 1
      && error[0] === this.translate.instant('App.NoCorporateLocationsAssigned');
  }
}
